import { GTMPageType } from '@/helpers/GTM/GTMParameters';
import {
  getCurrentPlatformTheme,
  getCurrentTargetPlatform,
  isPlatformIs24,
} from '@/helpers/platformDetection';
import {
  getLayoutSolutionRequestQueryParameters,
  isLayoutSolutionActive,
} from '@/resultlist/layoutSolutionRequest';
import { generateLayoutSolutionSRPRoute } from '@/resultlist/layoutSolutionSrpUri';
import type { AppStore } from '@/store/types';
import SeoEntryResultListPage from '@/views/SeoEntryResultListPage.vue';
import {
  HgSupportedLanguage,
  OfferType,
  resultListPageRouteMap,
  TargetPlatform,
} from '@smg-real-estate/hg-search-libs';
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

const HgPageNotFound = () => import('@smg-real-estate/domus/HgPageNotFound.vue');

/**
 * Common beforeEnter guard for layout solution routes
 * Modifies the route to use 404 component when layout solution is not active
 */
function createLayoutSolutionBeforeEnter(locale: string) {
  return (to: RouteLocationNormalized, from: RouteLocationNormalized, next: any) => {
    if (!isLayoutSolutionActive(to.query)) {
      to.matched[0].components = { default: HgPageNotFound };
      to.matched[0].props = {
        default: { theme: getCurrentPlatformTheme() },
      };
    }
    next();
  };
}

// Extended offer type constant for buy-or-rent combination
export const OFFER_TYPE_BUY_OR_RENT = 'buy-or-rent' as const;

// Extended offer type to include buy-or-rent combination
export type ExtendedOfferType = OfferType | typeof OFFER_TYPE_BUY_OR_RENT;

export const detailPageRouteMap: {
  [locale in HgSupportedLanguage]: { [offerType in ExtendedOfferType]: string };
} = {
  de: {
    [OfferType.RENT]: 'mieten',
    [OfferType.BUY]: 'kaufen',
    [OFFER_TYPE_BUY_OR_RENT]: 'mieten-oder-kaufen',
  },
  fr: {
    [OfferType.RENT]: 'louer',
    [OfferType.BUY]: 'acheter',
    [OFFER_TYPE_BUY_OR_RENT]: 'louer-ou-acheter',
  },
  en: {
    [OfferType.RENT]: 'rent',
    [OfferType.BUY]: 'buy',
    [OFFER_TYPE_BUY_OR_RENT]: 'buy-or-rent',
  },
  it: {
    [OfferType.RENT]: 'affittare',
    [OfferType.BUY]: 'acquistare',
    [OFFER_TYPE_BUY_OR_RENT]: 'affittare-o-acquistare',
  },
};

export function urlNameByGeoIdResolver(store: AppStore, targetPlatform: TargetPlatform) {
  return (locationId: string, language: HgSupportedLanguage): string | undefined => {
    const geoLocation = store.getters['geo/getGeoLocationById'](locationId);

    return targetPlatform === TargetPlatform.IMMOSCOUT24
      ? geoLocation?.is24UrlNames?.[language]
      : geoLocation?.urlNames[language];
  };
}

function getOtherLanguageRoute(
  store: AppStore,
  language: HgSupportedLanguage,
  currentRoute?: RouteLocationNormalized,
) {
  const searchModel = store.getters['resultList/lastSearchModel'];

  if (!searchModel) {
    return {
      path: null,
    };
  }

  const route = generateLayoutSolutionSRPRoute(
    language,
    searchModel,
    urlNameByGeoIdResolver(store, getCurrentTargetPlatform()),
  );

  // Preserve layout solution query parameters from current route if available
  if (currentRoute && isLayoutSolutionActive(currentRoute.query)) {
    const layoutSolutionParams = getLayoutSolutionRequestQueryParameters(currentRoute.query);

    route.query = {
      ...route.query,
      ...layoutSolutionParams,
    };
  }

  return route;
}

function generateHGResultListPageRoutes(
  store: AppStore,
  routeMap: Record<string, string>,
): RouteRecordRaw[] {
  return Object.entries(routeMap).map(([locale, path]) => {
    const routePath = `/:pathMatch(.*)/${path}`;
    return {
      path: routePath,
      name: `hg-${locale}`,
      component: SeoEntryResultListPage,
      props: (currentRoute: RouteLocationNormalized) => ({
        language: locale,
        query: currentRoute.query,
        path: currentRoute.path,
      }),
      beforeEnter: createLayoutSolutionBeforeEnter(locale),
      meta: {
        locale,
        gtmPageType: GTMPageType.RESULT_LIST_PAGE,
        getLanguageSwitchLink(route: RouteLocationNormalized, language: HgSupportedLanguage) {
          return getOtherLanguageRoute(store, language, route);
        },
      },
    };
  });
}

function generateIS24ResultListPageRoutes(
  store: AppStore,
  routeMap: Record<string, Record<string, string>>,
): RouteRecordRaw[] {
  return Object.entries(routeMap)
    .map(([locale, localizedOfferTypeMap]) => {
      return Object.entries(localizedOfferTypeMap).map(([offerType, path]) => {
        // For buy-or-rent, we don't set a specific offerType in meta
        // The search model will be determined by other query params
        const metaOfferType =
          offerType === OFFER_TYPE_BUY_OR_RENT ? undefined : (offerType as OfferType);

        return {
          path: `/${locale}/:objectType/${path}/:location`,
          name: `is24-${offerType}-${locale}`,
          component: SeoEntryResultListPage,
          props: (currentRoute: RouteLocationNormalized) => {
            // fullpath contains query params which are needed for the redirect
            // ensureNoAliases(currentRoute.fullPath, HgSupportedLanguage.EN);
            return {
              language: locale as HgSupportedLanguage,
              query: currentRoute.query,
              path: currentRoute.path,
            };
          },
          beforeEnter: createLayoutSolutionBeforeEnter(locale),
          meta: {
            locale: locale as HgSupportedLanguage,
            offerType: metaOfferType,
            gtmPageType: GTMPageType.RESULT_LIST_PAGE,
            getLanguageSwitchLink(route: RouteLocationNormalized, language: HgSupportedLanguage) {
              return getOtherLanguageRoute(store, language, route);
            },
          },
        };
      });
    })
    .flat();
}

function generateLayoutSolutionRoutes(store: AppStore): RouteRecordRaw[] {
  const isIs24 = isPlatformIs24();

  return isIs24
    ? generateIS24ResultListPageRoutes(store, detailPageRouteMap)
    : generateHGResultListPageRoutes(store, resultListPageRouteMap);
}

export default generateLayoutSolutionRoutes;
